package fr.frux.core.maps

import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/** Rayon d'angle de la plaque, rapporte a son cote (8 dp sur une plaque de 36 dp). */
internal const val PLAQUE_CORNER_FRACTION: Float = 8f / 36f

/** Fond de la pastille du badge : le brun presque noir des textes, lisible sur toute teinte. */
internal val PLAQUE_BADGE_COLOR: Color = Color(0xFF2B2822)

/**
 * Pastille d'accent d'une date, texte blanc dessus. Pas l'accent du theme : il donne ~3,4:1 sur du
 * texte de 12 sp, celui-ci ~4,9:1. Meme valeur dans les deux themes, la carte ne retheme pas ses sprites.
 * Publique pour que la legende dessine la meme pastille.
 */
val PLAQUE_BADGE_ACCENT_COLOR: Color = Color(0xFFA55F28)

/**
 * Cote du raster de la pastille. Etire par `iconTextFit` autour du texte, seule sa bande centrale
 * s'allonge (cf. [PLAQUE_BADGE_STRETCH_START]) : les extremites restent des demi-cercles.
 */
internal val PLAQUE_BADGE_PILL_SIZE: Dp = 20.dp

/** Debut de la bande etirable de la pastille, de part et d'autre de son centre. */
internal val PLAQUE_BADGE_STRETCH_START: Dp = 9.dp
internal val PLAQUE_BADGE_STRETCH_END: Dp = 11.dp

/**
 * Cle d'apparence complete d'une plaque : tout ce qui change son raster, et rien d'autre. La
 * selection choisit un second raster de meme cle, comme pour les epingles (voir
 * [selectedStyleKeys]). Serialisee sur chaque feature et reprise comme cle du `switch` de la
 * couche : les deux doivent rester le meme calcul.
 */
internal fun plaqueStyleKey(plaque: MapPlaque): String =
    "${plaque.tint.toCssHex()}|${plaque.soon}|${plaque.iconKey.orEmpty()}"

/** Cles dont la version agrandie doit etre enregistree : celles des plaques selectionnees. */
internal fun selectedPlaqueStyleKeys(plaques: List<MapPlaque>): Set<String> =
    plaques.filter { it.selected }.mapTo(mutableSetOf(), ::plaqueStyleKey)

/**
 * Icone complete d'une plaque, bord, plaque et glyphe d'un bloc, pour les memes raisons que
 * [CompositeMarkerPainter] : une seule couche de symboles, des teintes cuites dans le raster.
 *
 * Bord et plaque en `drawRoundRect`, jamais par un `VectorPainter` : rasterise a une taille non
 * entiere (le second raster de selection), son cache bitmap laisse un trait d'un pixel sur deux
 * bords (#650).
 */
internal class CompositePlaquePainter(
    private val rimColor: Color,
    private val plaqueColor: Color,
    private val glyph: Painter?,
    private val glyphColor: Color,
) : Painter() {
    override val intrinsicSize: Size get() = Size.Unspecified

    override fun DrawScope.onDraw() {
        val full = size.minDimension
        val plaqueSide = full / MARKER_RIM_SCALE
        val inset = (full - plaqueSide) / 2f
        val plaqueCorner = plaqueSide * PLAQUE_CORNER_FRACTION
        // Angle du bord concentrique a celui de la plaque : un rayon proportionnel au cote exterieur
        // amincirait le bord blanc dans les coins.
        drawRoundRect(
            color = rimColor,
            size = Size(full, full),
            cornerRadius = CornerRadius(plaqueCorner + inset),
        )
        drawRoundRect(
            color = plaqueColor,
            topLeft = Offset(inset, inset),
            size = Size(plaqueSide, plaqueSide),
            cornerRadius = CornerRadius(plaqueCorner),
        )
        if (glyph != null) {
            val side = plaqueSide * GLYPH_PLAQUE_FRACTION
            val glyphInset = (full - side) / 2f
            translate(glyphInset, glyphInset) {
                with(glyph) { draw(Size(side, side), colorFilter = ColorFilter.tint(glyphColor)) }
            }
        }
    }
}

/** Pastille du badge : un rectangle aux bouts ronds, etire par la couche autour du texte. */
internal class PlaqueBadgePillPainter(
    private val color: Color,
) : Painter() {
    override val intrinsicSize: Size get() = Size.Unspecified

    override fun DrawScope.onDraw() {
        drawRoundRect(color = color, cornerRadius = CornerRadius(size.minDimension / 2f))
    }
}
